import React, { FC, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components/native';

import dynamicSize from '@webtrader/core/dynamic-size';
import { IExperienceFormInputs } from '@webtrader/core/hooks/experienceDemo/useExperienceDemoForm';
import { useTranslation } from '@webtrader/core/i18n';

import Checkbox from '@/components/Checkbox';
import Text from '@/components/Text';
import TextInput from '@/components/TextInput';

import ConsentText from '../ConsentText';
import FormActions from './FormActions';

interface IBaseRegisterAccountFormProps {
  onSubmit: () => void;
}

const BaseRegisterAccountForm: FC<IBaseRegisterAccountFormProps> = ({ onSubmit }) => {
  const { formState, setValue, watch, trigger } = useFormContext<IExperienceFormInputs>();
  const { errors, isSubmitting } = formState;

  const [consent, email] = watch(['consent', 'email']);

  const { t } = useTranslation();

  const isDisabled = useMemo(() => !consent || !email || !!errors?.email, [consent, email, errors?.email]);

  return (
    <>
      <StyledInputWrapper>
        <TextInput
          validRegex={new RegExp('^[^\\s]*$')}
          label={t('Email')}
          value={email}
          placeholder="user@gmail.com"
          onChangeText={(value) => {
            setValue('email', value);
            trigger('email');
          }}
        />
        {!!errors?.email?.message && <StyledErrorText>{errors?.email?.message ?? ''}</StyledErrorText>}
      </StyledInputWrapper>
      <StyledInputWrapper>
        <Checkbox checked={consent} onCheck={() => setValue('consent', !consent)}>
          <StyledConsentTextWrapper>
            <ConsentText />
            {!!errors?.consent?.message && <StyledErrorText>{errors?.consent?.message ?? ''}</StyledErrorText>}
          </StyledConsentTextWrapper>
        </Checkbox>
        <FormActions isSubmitting={isSubmitting} onSubmit={onSubmit} isDisabled={isDisabled} />
      </StyledInputWrapper>
    </>
  );
};

export default BaseRegisterAccountForm;

const StyledInputWrapper = styled.View`
  margin-bottom: ${dynamicSize.convertSize(14)}px;
`;

export const StyledErrorText = styled(Text)`
  color: ${({ theme }) => theme.colors.error[0]};
  margin: ${dynamicSize.convertSize(4)}px 0 0 0;
`;

export const StyledErrorTextCenter = styled(StyledErrorText)`
  text-align: center;
`;

export const StyledConsentTextWrapper = styled.View`
  flex-shrink: 1;
  margin-top: -${dynamicSize.convertSize(2)}px;
`;
